import "./_recapCommande.scss";

import cx from "classnames";
import Decimal from "decimal.js";
import {ReactElement, useContext, useEffect, useState} from "react";
import {useWatch} from "react-hook-form";

import {HookFormContext} from "@/context/HookFormContext";
import {isPuriel} from "@/function/isPuriel";
import {
  convertCommandeApiToCommande,
  createCommande,
} from "@/function/operationCommercialeToole";
import {Magasin} from "@/ts/interface/magasin";
import {Commande, CommandeAPI} from "@/ts/interface/operationCommerciale";
import {LigneCommande} from "@/ts/interface/operationCommerciale";

const affichageTarifLigne = (
  quantite: number,
  label: string,
  montant: string,
  prixUnitaire?: Decimal | string,
): ReactElement => {
  return (
    <div className="recapCommande-ligne--prix" key={crypto.randomUUID()}>
      <div>
        <p className="recapCommande-strong">
          {label}
          <span className="recapCommande-strong">
            {`${quantite > 0 ? ` ${quantite} unité${isPuriel(quantite)} ` : ""}`}
          </span>
          <br />
          <span>
            {`${["Mail", "SMS"].includes(label) ? "" : "IMPRESSION + ENVOI "}`}
          </span>
        </p>
      </div>

      <div>
        <p className="recapCommande-strong">
          {`
          ${new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(Number(montant))}`}

          <br />
          {prixUnitaire && prixUnitaire !== "0" ? (
            <span>{`Soit : ${new Intl.NumberFormat("fr-FR", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Number(prixUnitaire))}/u`}</span>
          ) : null}
        </p>
      </div>
    </div>
  );
};

const affichageLigneItem = (
  itemLabel: string,
  formatLabel: string,
  options: JSX.Element | null,
  livraisons: ReactElement[] | null,
  affichageForfaitItem: ReactElement | null,
): ReactElement => {
  return (
    <div
      className="recapCommande-ligne"
      key={`${itemLabel} ${formatLabel} ${crypto.randomUUID()}`}>
      <div className="recapCommande-offre--information">
        <p className="recapCommande-strong">
          {itemLabel} {formatLabel}
        </p>

        {options}
      </div>
      <div className="recapCommande-ligne--tarif">
        {livraisons} {affichageForfaitItem}
      </div>
    </div>
  );
};

function RecapCommande({
  magasins,
  commande = undefined,
  fullDetail = false,
}: {
  magasins: Magasin[];
  commande?: CommandeAPI;
  fullDetail?: boolean;
}): ReactElement {
  let allValues = null;
  const [currentCommande, setCurrentCommande] = useState<Commande | undefined>(
    undefined,
  );
  //Pour le rendu du récapitulatif de la commande quand on est dans le formulaire de commande
  if (HookFormContext && commande === undefined) {
    const context = useContext(HookFormContext);

    if (!context) {
      throw new Error("HookFormContext is not provided.");
    }

    const {control} = context;
    allValues = useWatch({control});
    // écoute tous les champs
  }
  useEffect(() => {
    if (commande) {
      setCurrentCommande(convertCommandeApiToCommande(commande));
    }
  }, [commande]);
  // Forcer le re-render
  useEffect(() => {
    if (allValues) {
      // On met 0 pour l'opération ID, cette commande ne sert que pour de l'affichage, l'id n'a donc pas d'importance.
      setCurrentCommande(createCommande(allValues, 0));
    }
  }, [allValues]);

  // Créer un tableau avec toutes les lignes de la commande par magasin et par offre !
  const magCommande: {
    magasin: Magasin;
    offres: {
      offre_id?: number;
      offre_label?: string;
      ligne_commande?: LigneCommande[];
    }[];
  }[] = magasins
    .map(mag => {
      // Récupère les offres qui sont dans la commande
      const offreInCommande = currentCommande?.ligne_commande
        ?.filter(
          (ligne, index, self) =>
            index === self.findIndex(t => t.offre_id === ligne.offre_id),
        )
        .map(ligne => {
          return {offre_id: ligne.offre_id, offre_label: ligne.offre_label};
        });
      // Récupère les lignes du magasin en cours
      const ligneMagasin = currentCommande?.ligne_commande?.filter(ligne => {
        return ligne.magasin_id == mag.magasin_id;
      });

      // Associe les lignes du magasin à l'offre courante.
      const lignePerOffre = offreInCommande
        ?.map(offre => {
          const ligneInOffreAndMagasin = ligneMagasin?.filter(ligne => {
            return ligne.offre_id === offre.offre_id;
          });
          return {...offre, ligne_commande: ligneInOffreAndMagasin};
        })
        // Retire les offres qui n'ont pas ligne
        .filter(offre => {
          return (offre?.ligne_commande?.length || 0) > 0;
        });
      return {magasin: mag, offres: lignePerOffre || []};
    })
    // retire les magasins qui n'ont pas d'offre
    .filter(mag => {
      return mag.offres.length > 0;
    });

  const recapCommande = magCommande.map(({magasin, offres}, index) => {
    let totalMagasin: Decimal = new Decimal(0);

    const allOffres = offres.map(offre => {
      const allLigneInOffre = offre?.ligne_commande?.map(ligne => {
        const options =
          ligne?.option_commande && ligne?.option_commande.length > 0 ? (
            <div
              className="recapCommande-options"
              key={`${ligne.item_id} ${ligne.magasin_id}`}>
              <ul>
                {ligne.option_commande.map(option => {
                  return (
                    <li
                      key={`${ligne.item_id} ${ligne.magasin_id} ${option.item_option_id}`}>
                      {option.item_option_label}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null;

        const livraisons = ligne?.livraison_commande?.map(liv => {
          const prixUnitaire = new Decimal(liv.prix_unitaire).isNaN()
            ? new Decimal("0")
            : new Decimal(liv.prix_unitaire);
          let montantLigne = new Decimal(prixUnitaire);
          montantLigne = montantLigne
            .mul(liv.quantite)
            .toDecimalPlaces(2, Decimal.ROUND_DOWN);

          totalMagasin = totalMagasin.plus(montantLigne);
          return affichageTarifLigne(
            liv.quantite,
            liv.livraison_label || "",
            montantLigne.toFixed(2),
            prixUnitaire,
          );
        });
        let affichageForfaitItem = null;
        if (ligne?.item_forfait && parseFloat(ligne?.item_forfait) > 0) {
          affichageForfaitItem = affichageTarifLigne(
            0,
            `Forfait ${ligne.item_label}`,
            new Decimal(ligne.item_forfait).toFixed(2),
          );
          totalMagasin = totalMagasin.plus(ligne.item_forfait);
        }
        return affichageLigneItem(
          ligne.item_label || "",
          ligne.item_format_label || "",
          options,
          livraisons || null,
          affichageForfaitItem,
        );
      });

      let affichageOffreForfait = null;
      // verifie si l'offre possède un forfait
      if (offre.ligne_commande?.[0].offre_forfait) {
        const OffreForfait = new Decimal(
          offre.ligne_commande?.[0].offre_forfait,
        );

        const offreHaveItem = offre.ligne_commande?.[0].item_id;
        // Affiche le forfait de l'offre si le montant n'est pas Zéro
        affichageOffreForfait = OffreForfait.isZero()
          ? null
          : affichageLigneItem(
              offreHaveItem ? "Forfait Offre : " : "Participation à l'offre :",
              offre.offre_label || "",
              null,
              null,
              affichageTarifLigne(0, "", OffreForfait.toFixed(2) || "0", "0"),
            );
        totalMagasin = totalMagasin.plus(OffreForfait);
      }
      console.log(offre);
      return (
        <div className="recapCommande-offre" key={offre.offre_id}>
          <h4>
            {offre.offre_label}
            <span>
              {` - Repiquage             ${offre?.ligne_commande?.[0].repiquage} `}
            </span>
          </h4>
          {allLigneInOffre}
          {affichageOffreForfait}
        </div>
      );
    });
    return (
      <div
        className={cx("recapCommande-magasin", index % 2 ? "bg-grey" : "")}
        key={`${magasin.magasin_id} ${index}`}>
        <h3>{magasin.raison_sociale}</h3>
        <div>{allOffres}</div>
        <p className="recapCommande-total recapCommande-strong">{`Total  : ${new Intl.NumberFormat(
          "fr-FR",
          {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        ).format(Number(totalMagasin.toFixed(2)))}`}</p>
      </div>
    );
  });
  const livraisonUnique = currentCommande?.ligne_commande?.some(ligne => {
    return ligne.magasin_livraison_id !== ligne.magasin_id;
  });
  const magasinLivraison = livraisonUnique
    ? magasins?.find(magasin => {
        return (
          magasin?.magasin_id ===
          currentCommande?.ligne_commande?.[0].magasin_livraison_id
        );
      })
    : null;
  return (
    <div className="recapCommande">
      {fullDetail ? (
        <>
          <p>
            {`${currentCommande?.commentaire ? "Commentaire de la commande : " + currentCommande?.commentaire : ""}`}
          </p>
          <br />
          <strong>
            {`${livraisonUnique ? "Livraison commune dans ce point de vente : " + magasinLivraison?.raison_sociale : "Livraison au point de vente "}`}
          </strong>
        </>
      ) : null}

      {recapCommande}
      {recapCommande.length > 0 ? (
        <p className="recapCommande-total--commande recapCommande-strong ">{`Total de la commande : ${new Intl.NumberFormat(
          "fr-FR",
          {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        ).format(Number(currentCommande?.montant_total) || 0)}`}</p>
      ) : null}
    </div>
  );
}

export {RecapCommande};
